import { crsLeadSubmitted } from "@/lib/analytics-events";
import { useState, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Check, CheckCircle2, Loader2 } from "lucide-react";

import { submitEnquiry } from "@/lib/forms.functions";
import { submissionSchema } from "@/lib/form-schema";
import {
  FINANCING_FORM_HOMEOWNER_OPTIONS,
  FINANCING_FORM_SERVICE_OPTIONS,
  FINANCING_STANDING_DISCLAIMER,
} from "@/content/landing/financing";
import { isValidUkPhone } from "@/lib/phone-validation";
import { cn } from "@/lib/utils";

const PHONE_LABEL = "01656 339844";
const PHONE_TEL = "tel:+441656339844";

const UK_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

function scrollToFirstFormIssue(form: HTMLFormElement) {
  requestAnimationFrame(() => {
    const target =
      form.querySelector<HTMLElement>('[aria-invalid="true"]') ??
      form.querySelector<HTMLElement>(".text-destructive") ??
      form.querySelector<HTMLElement>("[class*='border-destructive']");
    target?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  });
}

export interface FinancingEnquiryFormProps {
  idPrefix?: string;
  sourcePage?: string;
  defaultPostcode?: string;
  variant?: "hero" | "default";
}

/** Shared enquiry form for the /financing hero and closing CTA. */
export function FinancingEnquiryForm({
  idPrefix = "fe",
  sourcePage = "/financing",
  defaultPostcode = "",
  variant = "default",
}: FinancingEnquiryFormProps) {
  const submit = useServerFn(submitEnquiry);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [selectedServices, setSelectedServices] = useState<Set<string>>(new Set());

  const isHero = variant === "hero";
  const labelCls = cn("block text-sm font-semibold mb-1.5", isHero && "text-primary-foreground");
  const inputCls = isHero
    ? "w-full rounded-lg border border-white/50 bg-white/15 px-3 py-2.5 text-base text-white placeholder:text-white/75 focus:outline-none focus:ring-2 focus:ring-white/40"
    : "w-full rounded-lg border border-input bg-background px-3 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-ring";
  const selectCls = cn(inputCls, isHero ? "[color-scheme:dark]" : "[color-scheme:light]");
  const errCls = "text-xs text-destructive mt-1";
  const footnoteCls = cn(
    "text-[11px] text-center leading-relaxed",
    isHero ? "text-primary-foreground/80" : "text-muted-foreground",
  );
  const privacyCls = cn(
    "text-xs text-center leading-relaxed mt-3",
    isHero ? "text-primary-foreground/85" : "text-muted-foreground",
  );

  function toggleService(label: string) {
    setSelectedServices((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next.services_interested;
      return next;
    });
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    setStatus("submitting");
    setErrors({});
    setFormError(null);

    const fd = new FormData(form);
    const name = ((fd.get("name") as string) || "").trim();
    const phone = ((fd.get("phone") as string) || "").trim();
    const email = ((fd.get("email") as string) || "").trim();
    const postcode = ((fd.get("postcode") as string) || "").trim();
    const homeownerStatus = ((fd.get("homeowner_status") as string) || "").trim();
    const servicesInterested = [...selectedServices];

    const localErrors: Record<string, string> = {};
    if (!name) localErrors.name = "Name is required";
    if (!phone) {
      localErrors.phone = "Phone is required";
    } else if (!isValidUkPhone(phone)) {
      localErrors.phone = "Please enter a valid UK phone number";
    }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      localErrors.email = "Please enter a valid email address";
    }
    if (!postcode) {
      localErrors.postcode = "Postcode is required";
    } else if (!UK_POSTCODE.test(postcode)) {
      localErrors.postcode = "Please enter a valid UK postcode";
    }
    if (!homeownerStatus) {
      localErrors.homeowner_status = "Please tell us if you are the homeowner";
    }
    if (servicesInterested.length === 0) {
      localErrors.services_interested = "Please select at least one service";
    }

    if (Object.keys(localErrors).length) {
      setErrors(localErrors);
      setStatus("idle");
      scrollToFirstFormIssue(form);
      return;
    }

    const raw = {
      form_type: "quote" as const,
      name,
      phone,
      email: email || undefined,
      postcode,
      homeowner_status: homeownerStatus,
      services_interested: servicesInterested,
      service_of_interest: "Finance enquiry",
      source_page: sourcePage,
      honeypot: (fd.get("company_website") as string) || "",
    };

    const parsed = submissionSchema.safeParse(raw);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        fieldErrors[String(issue.path[0] ?? "form")] = issue.message;
      }
      setErrors(fieldErrors);
      setStatus("idle");
      const visibleFields = new Set([
        "name",
        "phone",
        "email",
        "postcode",
        "homeowner_status",
        "services_interested",
      ]);
      if (Object.keys(fieldErrors).some((key) => !visibleFields.has(key))) {
        setFormError("We couldn't send your enquiry. Please check your details and try again.");
      }
      scrollToFirstFormIssue(form);
      return;
    }

    try {
      const result = await submit({ data: parsed.data });
      if (result.success) {
        if (result.id) crsLeadSubmitted({ leadId: result.id, formId: "financing_enquiry", leadType: "finance" });
        setStatus("success");
      } else {
        setFormError(result.error ?? "Something went wrong. Please try again.");
        setStatus("error");
        scrollToFirstFormIssue(form);
      }
    } catch (err) {
      console.error(err);
      setFormError(`Network error. Please call us on ${PHONE_LABEL}.`);
      setStatus("error");
      scrollToFirstFormIssue(form);
    }
  }

  if (status === "success") {
    return (
      <div
        className={cn(
          "rounded-xl border p-6 md:p-8 text-center",
          isHero
            ? "border-white/25 bg-white/10 text-primary-foreground"
            : "border-border bg-background",
        )}
      >
        <CheckCircle2
          className={cn("mx-auto h-10 w-10 mb-4", isHero ? "text-accent" : "text-accent")}
          aria-hidden
        />
        <p
          className={cn(
            "text-base md:text-lg font-semibold mb-2",
            isHero ? "text-primary-foreground" : "text-foreground",
          )}
        >
          Thanks. We&apos;ve got your enquiry.
        </p>
        <p className={cn("text-sm leading-relaxed", isHero ? "text-primary-foreground/90" : "text-muted-foreground")}>
          One of the team will call you to talk through your options. Or call us on{" "}
          <a href={PHONE_TEL} className="font-semibold text-accent hover:underline">
            {PHONE_LABEL}
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-3 sm:gap-4" noValidate>
      <input
        type="text"
        name="company_website"
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden
      />

      <div>
        <label htmlFor={`${idPrefix}-name`} className={labelCls}>
          Name <span className="text-destructive">*</span>
        </label>
        <input id={`${idPrefix}-name`} name="name" required className={inputCls} />
        {errors.name && <p className={errCls}>{errors.name}</p>}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-phone`} className={labelCls}>
          Phone <span className="text-destructive">*</span>
        </label>
        <input
          id={`${idPrefix}-phone`}
          name="phone"
          required
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          className={inputCls}
        />
        {errors.phone && <p className={errCls}>{errors.phone}</p>}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-postcode`} className={labelCls}>
          Postcode <span className="text-destructive">*</span>
        </label>
        <input
          id={`${idPrefix}-postcode`}
          name="postcode"
          required
          defaultValue={defaultPostcode}
          className={inputCls}
          placeholder="Confirms England / Wales"
          autoComplete="postal-code"
        />
        {errors.postcode && <p className={errCls}>{errors.postcode}</p>}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-email`} className={labelCls}>
          Email (optional)
        </label>
        <input id={`${idPrefix}-email`} name="email" type="email" className={inputCls} />
        {errors.email && <p className={errCls}>{errors.email}</p>}
      </div>

      <div className="sm:col-span-2 space-y-1.5">
        <label htmlFor={`${idPrefix}-homeowner`} className={labelCls}>
          Are you the homeowner? <span className="text-destructive">*</span>
        </label>
        <select
          id={`${idPrefix}-homeowner`}
          name="homeowner_status"
          required
          defaultValue=""
          className={selectCls}
        >
          <option value="" disabled>
            Select an option
          </option>
          {FINANCING_FORM_HOMEOWNER_OPTIONS.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        {errors.homeowner_status && <p className={errCls}>{errors.homeowner_status}</p>}
      </div>

      <fieldset className="sm:col-span-2">
        <legend className={cn(labelCls, "mb-2")}>
          Which services are you interested in? <span className="text-destructive">*</span>
        </legend>
        <div className="flex flex-wrap gap-2">
          {FINANCING_FORM_SERVICE_OPTIONS.map((label) => {
            const selected = selectedServices.has(label);
            return (
              <button
                key={label}
                type="button"
                aria-pressed={selected}
                onClick={() => toggleService(label)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors",
                  selected
                    ? "border-accent bg-accent text-accent-foreground shadow-sm"
                    : isHero
                      ? "border-white/35 bg-white/10 text-primary-foreground hover:bg-white/15"
                      : "border-border bg-background text-foreground hover:bg-secondary/50",
                )}
              >
                {selected && <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} aria-hidden />}
                {label}
              </button>
            );
          })}
        </div>
        {errors.services_interested && <p className={errCls}>{errors.services_interested}</p>}
      </fieldset>

      {formError && (
        <div className="sm:col-span-2 rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {formError}
        </div>
      )}

      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3.5 font-semibold shadow-cta hover:bg-accent/90 transition-colors w-full disabled:opacity-60"
        >
          {status === "submitting" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Sending...
            </>
          ) : (
            <>
              Check My Options
              <ArrowRight className="h-4 w-4" aria-hidden />
            </>
          )}
        </button>

        <p className={privacyCls}>
          By submitting, you agree to CRS Energy contacting you about your enquiry. See our{" "}
          <Link to="/privacy" className="font-semibold text-accent hover:underline">
            Privacy Policy
          </Link>
          .
        </p>

        <p className={cn(footnoteCls, "mt-2")}>{FINANCING_STANDING_DISCLAIMER}</p>
      </div>
    </form>
  );
}

/** @alias FinancingEnquiryForm — shared /financing page enquiry form */
export const EnquiryForm = FinancingEnquiryForm;
